import {texture} from "p3";
import definitions from "../../definitions";
import GameScene from "../../scenes/GameScene";

export default class TutorialControls extends PIXI.Container {
    constructor() {
        super();

        this._keys = null;
        this._spacebar = null;

        this._movementTween = null;
        this._interactionTween = null;
        this._handTween = null;

        this._state = definitions.TUTORIAL_STATE.NONE;

        this.init();
    }

    init() {
        this._keys = new PIXI.Container();
        this._keys.visible = false;
        this.addChild(this._keys);

        this._keys.left = this._createLeftKey();
        this._keys.left.position.set(-80, 0);
        this._keys.left.play(true);
        this._keys.addChild(this._keys.left);

        this._keys.right = this._createRightKey();
        this._keys.right.position.set(80, 0);
        this._keys.right.play(true);
        this._keys.addChild(this._keys.right);

        this._keys.up = this._createUpKey();
        this._keys.up.position.set(0, -76);
        this._keys.up.play(true);
        this._keys.addChild(this._keys.up);

        this._keys.down = this._createDownKey();
        this._keys.down.position.set(0, 0);
        this._keys.down.play(true);
        this._keys.addChild(this._keys.down);

        this._spacebar = this._createSpaceKey();
        this._spacebar.position.set(-2, 0);
        this._spacebar.play(true);
        this._spacebar.visible = false;
        this.addChild(this._spacebar);

        this._touch = new PIXI.Container();
        this._touch.visible = false;
        this.addChild(this._touch);

        this._touch.joystick = new PIXI.Container();
        this._touch.addChild(this._touch.joystick);

        this._touch.joystick.area = new PIXI.Sprite(texture("joystick_area"));
        this._touch.joystick.area.alpha = 0.8;
        this._touch.joystick.area.anchor.set(0.5);
        this._touch.joystick.addChild(this._touch.joystick.area);

        this._touch.joystick.control = new PIXI.Sprite(texture("joystick"));
        this._touch.joystick.control.alpha = 0.8;
        this._touch.joystick.control.anchor.set(0.5);
        this._touch.joystick.addChild(this._touch.joystick.control);

        let frames = [texture("tutorial_hand_point")];
        let sequence = new P3.MovieClipSequence(frames, "release");

        frames = [texture("tutorial_hand_press")];
        sequence.addTextures(frames, "press");

        this._touch.hand = new P3.MovieClip(sequence, "release", false);
        this._touch.hand.anchor.set(0.28, 0.16);
        this._touch.hand.animationSpeed = 1;
        this._touch.addChild(this._touch.hand);

        frames = [texture("tutorial_hand_point"), texture("tutorial_hand_press")];
        sequence = new P3.MovieClipSequence(frames, "release");

        this._pointer = new P3.MovieClip(sequence, "release", false);
        this._pointer.anchor.set(0.28, 0.16);
        this._pointer.animationSpeed = 2;
        this._pointer.play(true);
        this._pointer.visible = false;
        this.addChild(this._pointer);
    }

    destroy() {
        this.stopAnimations();

        super.destroy({ children: true });
    }

    update() {
        if (this._keys.visible) {
            this._keys.left.update(window.dt);
            this._keys.right.update(window.dt);
            this._keys.up.update(window.dt);
            this._keys.down.update(window.dt);
        }

        if (this._spacebar) {
            this._spacebar.update(window.dt);
        }

        this._touch.joystick.control.position = this._touch.hand.position;

        if (this._pointer.visible) {
            this._pointer.update(window.dt);
        }
    }

    /**
     * @param {boolean} [loop=false]
     * @return {Promise}
     */
    animateHand(loop= false) {
        return new Promise((resolve) => {
            if (this._handTween) {
                GameScene.active.removeTween(this._handTween, true);
                this._handTween = null;
            }

            const repeat = loop ? -1 : 0;
            this._handTween = gsap.timeline({ delay: 1.4, repeat: repeat, onComplete: resolve });
            GameScene.active.addTween(this._handTween);

            this._handTween.add(gsap.to(this._touch.hand, 0.8, {
                x: 80,
                ease: Power2.easeInOut,
                onStart: () => this._touch.hand.gotoAndStop("press")
            }));
            this._handTween.add(gsap.to(this._touch.hand, 1.6, {
                delay: 0.2,
                x: -80,
                ease: Power3.easeInOut
            }));
            this._handTween.add(gsap.to(this._touch.hand, 0.8, {
                x: 0,
                ease: Power2.easeInOut
            }));
            this._handTween.add(gsap.to(this._touch.hand, 0.8, {
                x: 0,
                y: -80,
                ease: Power2.easeInOut
            }));
            this._handTween.add(gsap.to(this._touch.hand, 1.6, {
                x: 0,
                y: 80,
                ease: Power3.easeInOut
            }));
            this._handTween.add(gsap.to(this._touch.hand, 0.8, {
                y: 0,
                ease: Power2.easeInOut,
                onComplete: () => this._touch.hand.gotoAndStop("release")
            }));
            this._handTween.add(gsap.delayedCall(1, () => {}))
        });
    }

    stopAnimations() {
        if (this._movementTween) {
            GameScene.active.removeTween(this._movementTween, true);
            this._movementTween = null;
        }
        if (this._interactionTween) {
            GameScene.active.removeTween(this._interactionTween, true);
            this._interactionTween = null;
        }
        if (this._handTween) {
            GameScene.active.removeTween(this._handTween, true);
            this._handTween = null;
        }
    }

    /**
     * @param {number} state
     * @param {boolean} [animate=false]
     */
    setState(state, animate) {
        if (this._state !== state) {
            this._state = state;

            switch (this._state) {
                case definitions.TUTORIAL_STATE.NONE: {
                    this._hideMovement().then();
                    this._hideInteraction().then();
                    break;
                }
                case definitions.TUTORIAL_STATE.MOVEMENT: {
                    this._showMovement().then();
                    this._hideInteraction().then();
                    break;
                }
                case definitions.TUTORIAL_STATE.INTERACTION: {
                    this._hideMovement().then();
                    this._showInteraction().then();
                    break;
                }
            }
        }
    }

    /**
     * @return {Promise}
     * @private
     */
    _showMovement() {
        return new Promise((resolve) => {
            if (this._movementTween) {
                GameScene.active.removeTween(this._movementTween, true);
                this._movementTween = null;
            }

            if (PIXI.utils.isMobile.any) {
                this._touch.alpha = 0;
                this._touch.visible = true;
                this._movementTween = gsap.to(this._touch, 0.34, {
                    delay: 1,
                    alpha: 1,
                    ease: Power1.easeInOut,
                    onStart: () => this.animateHand(true),
                    onComplete: resolve
                });
                GameScene.active.addTween(this._movementTween);
            } else {
                this._keys.alpha = 0;
                this._keys.visible = true;
                this._movementTween = gsap.to(this._keys, 0.34, {
                    delay: 1,
                    alpha: 1,
                    ease: Power1.easeInOut,
                    onComplete: resolve
                });
                GameScene.active.addTween(this._movementTween);
            }
        });
    }

    /**
     * @return {Promise}
     * @private
     */
    _hideMovement() {
        return new Promise((resolve) => {
            if (this._movementTween) {
                GameScene.active.removeTween(this._movementTween, true);
                this._movementTween = null;
            }

            if (PIXI.utils.isMobile.any) {
                this._movementTween = gsap.to(this._touch, 0.34, {
                    alpha: 0,
                    ease: Power1.easeInOut,
                    onComplete: () => {
                        if (this._handTween) {
                            GameScene.active.removeTween(this._handTween, true);
                            this._handTween = null;
                        }
                        this._touch.alpha = 1;
                        this._touch.visible = false;
                        resolve();
                    }
                });
                GameScene.active.addTween(this._movementTween);
            } else {
                this._movementTween = gsap.to(this._keys, 0.34, {
                    alpha: 0,
                    ease: Power1.easeInOut,
                    onComplete: () => {
                        this._keys.alpha = 1;
                        this._keys.visible = false;
                        resolve();
                    }
                });
                GameScene.active.addTween(this._movementTween);
            }
        });
    }

    /**
     * @return {Promise}
     * @private
     */
    _showInteraction() {
        return new Promise((resolve) => {
            if (this._interactionTween) {
                GameScene.active.removeTween(this._interactionTween, true);
                this._interactionTween = null;
            }

            if (PIXI.utils.isMobile.any) {
                this._pointer.alpha = 0;
                this._pointer.visible = true;
                this._interactionTween = gsap.to(this._pointer, 0.34, {
                    delay: 0.2,
                    alpha: 1,
                    ease: Power1.easeInOut,
                    onComplete: resolve
                });
                GameScene.active.addTween(this._interactionTween);
            } else {
                this._spacebar.alpha = 0;
                this._spacebar.visible = true;
                this._interactionTween = gsap.to(this._spacebar, 0.34, {
                    delay: 0.2,
                    alpha: 1,
                    ease: Power1.easeInOut,
                    onComplete: resolve
                });
                GameScene.active.addTween(this._interactionTween);
            }
        });
    }

    /**
     * @return {Promise}
     * @private
     */
    _hideInteraction() {
        return new Promise((resolve) => {
            if (this._interactionTween) {
                GameScene.active.removeTween(this._interactionTween, true);
                this._interactionTween = null;
            }

            if (PIXI.utils.isMobile.any) {
                this._interactionTween = gsap.to(this._pointer, 0.34, {
                    alpha: 0,
                    ease: Power1.easeInOut,
                    onComplete: () => {
                        this._pointer.alpha = 1;
                        this._pointer.visible = false;
                        resolve();
                    }
                });
                GameScene.active.addTween(this._interactionTween);
            } else {
                this._interactionTween = gsap.to(this._spacebar, 0.34, {
                    alpha: 0,
                    ease: Power1.easeInOut,
                    onComplete: () => {
                        this._spacebar.alpha = 1;
                        this._spacebar.visible = false;
                        resolve();
                    }
                });
                GameScene.active.addTween(this._interactionTween);
            }
        });
    }

    /**
     * @return {P3.MovieClip}
     * @private
     */
    _createLeftKey() {
        const up = texture("tutorial_key_left_off");
        const down = texture("tutorial_key_left_on");

        let frames = [down, up, up, up, up, up, up, up, up, up, up, up, up, up];
        const sequence = new P3.MovieClipSequence(frames);
        return this._createKey(sequence);
    }

    /**
     * @return {P3.MovieClip}
     * @private
     */
    _createRightKey() {
        const up = texture("tutorial_key_right_off");
        const down = texture("tutorial_key_right_on");

        let frames = [up, up, down, up, up, up, up, up, up, up, up, up, up, up];
        const sequence = new P3.MovieClipSequence(frames);
        return this._createKey(sequence);
    }

    /**
     * @return {P3.MovieClip}
     * @private
     */
    _createUpKey() {
        const up = texture("tutorial_key_up_off");
        const down = texture("tutorial_key_up_on");

        let frames = [up, up, up, up, up, down, up, up, up, up, up, up, up, up];
        const sequence = new P3.MovieClipSequence(frames);
        return this._createKey(sequence);
    }

    /**
     * @return {P3.MovieClip}
     * @private
     */
    _createDownKey() {
        const up = texture("tutorial_key_down_off");
        const down = texture("tutorial_key_down_on");

        let frames = [up, up, up, up, up, up, up, up, down, up, up, up, up, up];
        const sequence = new P3.MovieClipSequence(frames);
        return this._createKey(sequence);
    }

    /**
     * @return {P3.MovieClip}
     * @private
     */
    _createSpaceKey() {
        const up = texture("tutorial_space_off");
        const down = texture("tutorial_space_on");

        let frames = [up, up, down, up, up];
        const sequence = new P3.MovieClipSequence(frames);
        return this._createKey(sequence);
    }

    /**
     * @param {P3.MovieClipSequence} sequence
     * @private
     */
    _createKey(sequence) {
        const key = new P3.MovieClip(sequence, "default", false);
        key.anchor.set(0.5);
        key.animationSpeed = 4;
        return key;
    }

    /**
     * @return {number}
     */
    get state() {
        return this._state;
    }

    /**
     * @return {PIXI.Sprite}
     */
    get pointer() {
        return this._pointer;
    }
}